import {
	type ChartCardConfig,
	type ChartDataConfig,
	knownGranularity,
	type MetricConfig,
	type OrderConfig,
} from '../../../../models/chart-config.model';
import { CardType } from '../../../../models/card-model';
import { getPrimaryChartTimeDimension } from '../../../../utils/chart-card-date-range.util';
import {
	isTimeSeriesChartType,
	usesCategoryAxisChartType,
} from './chart-data-tab-axis.util';

export function timeDimensionOrderKey(
	data: ChartDataConfig | undefined,
	explicitDimension?: string
): string {
	const explicit = explicitDimension?.trim();
	if (explicit) {
		return explicit;
	}
	const fromSingle = data?.timeDimension?.dimension?.trim();
	if (fromSingle) {
		return fromSingle;
	}
	const primary = getPrimaryChartTimeDimension({
		data: data ?? {},
	} as ChartCardConfig);
	const fromPrimary =
		typeof primary?.['dimension'] === 'string'
			? primary['dimension'].trim()
			: '';
	return fromPrimary;
}

export function categoryDimensionOrderKey(
	data: ChartDataConfig | undefined,
	explicitDimension?: string
): string {
	const explicit = explicitDimension?.trim();
	if (explicit) {
		return explicit;
	}
	return data?.dimensions?.[0]?.name?.trim() ?? '';
}

export interface BuildDefaultChartDataOrderOptions {
	timeDimensionField?: string;
	categoryDimensionField?: string;
	metrics?: MetricConfig[];
}

type OrderDirection = 'asc' | 'desc';

function parseOrderDirection(value: unknown): OrderDirection | undefined {
	if (value === 'asc' || value === 'ASC') {
		return 'asc';
	}
	if (value === 'desc' || value === 'DESC') {
		return 'desc';
	}
	return undefined;
}

/**
 * Normalizes AppSync `AWSJSON` (string or map) into client `OrderConfig`.
 * Verified on `jepto_development` AppSync (`CardDataConfigInput.order: AWSJSON`).
 */
export function normalizeOrderFromWire(
	order: unknown
): OrderConfig | undefined {
	if (!order) {
		return undefined;
	}
	if (typeof order === 'string') {
		const trimmed = order.trim();
		if (!trimmed) {
			return undefined;
		}
		try {
			return orderConfigForPersistence(JSON.parse(trimmed) as OrderConfig);
		} catch {
			return undefined;
		}
	}
	if (Array.isArray(order)) {
		for (const item of order) {
			if (!item || typeof item !== 'object') {
				continue;
			}
			const record = item as Record<string, unknown>;
			const field =
				typeof record.field === 'string'
					? record.field.trim()
					: typeof record.sortField === 'string'
						? record.sortField.trim()
						: '';
			const direction =
				parseOrderDirection(record.sortBy) ??
				parseOrderDirection(record.direction);
			if (field && direction) {
				return { [field]: direction };
			}
		}
		return undefined;
	}
	return orderConfigForPersistence(order as OrderConfig);
}

function validOrderEntries(
	order: unknown
): [string, OrderDirection][] {
	if (!order || typeof order !== 'object' || Array.isArray(order)) {
		return [];
	}
	return Object.entries(order as Record<string, unknown>).filter(
		(entry): entry is [string, OrderDirection] => {
			const [key, direction] = entry;
			return (
				key.trim().length > 0 &&
				(direction === 'asc' || direction === 'desc')
			);
		}
	);
}

/** Single-field order for API persistence; omits empty or invalid maps. */
export function orderConfigForPersistence(
	order: OrderConfig | undefined | null
): OrderConfig | undefined {
	const entries = validOrderEntries(order);
	if (entries.length === 0) {
		return undefined;
	}
	const [key, direction] = entries[0];
	return { [key]: direction };
}

/** Maps client `OrderConfig` to AppSync `CardDataConfigInput.order` (`AWSJSON` string). */
export function orderConfigToAwsJsonForGql(order: unknown): string | undefined {
	const map =
		normalizeOrderFromWire(order) ??
		orderConfigForPersistence(order as OrderConfig);
	if (!map) {
		return undefined;
	}
	try {
		return JSON.stringify(map);
	} catch {
		return undefined;
	}
}

/** Table cards: grouping dimension or first metric descending. */
function buildDefaultTableDataOrder(
	data: ChartDataConfig | undefined,
	options: BuildDefaultChartDataOrderOptions = {}
): OrderConfig {
	const key = categoryDimensionOrderKey(
		data,
		options.categoryDimensionField
	);
	if (key) {
		return { [key]: 'desc' };
	}
	const metric =
		options.metrics?.[0]?.name?.trim() ??
		data?.metrics?.[0]?.name?.trim();
	return metric ? { [metric]: 'desc' } : {};
}

/**
 * Defaults for `data.order` by card type.
 * Chart cards delegate to chart-type rules; table cards use grouping/metric sort.
 */
export function buildDefaultDataOrder(
	cardType: CardType,
	chartType: string,
	data: ChartDataConfig | undefined,
	options: BuildDefaultChartDataOrderOptions = {}
): OrderConfig {
	if (cardType === CardType.Table) {
		return buildDefaultTableDataOrder(data, options);
	}
	return buildDefaultChartDataOrder(chartType, data, options);
}

/** Uses saved order when present; otherwise card-type defaults. */
export function resolveDataOrder(
	cardType: CardType,
	chartType: string,
	data: ChartDataConfig | undefined,
	options: BuildDefaultChartDataOrderOptions = {}
): OrderConfig {
	const entries = validOrderEntries(normalizeOrderFromWire(data?.order));
	if (entries.length === 0) {
		return buildDefaultDataOrder(cardType, chartType, data, options);
	}

	const [key, direction] = entries[0];
	return { [key]: direction };
}

/**
 * Chart-type defaults for `data.order`:
 * - Line / area: time dimension ascending (chronological).
 * - Bar / column / donut: category dimension descending (largest slice first),
 *   except the card's date as the category with a granularity set (grouped):
 *   ascending (Monday…Sunday, Jan…Dec).
 */
export function buildDefaultChartDataOrder(
	chartType: string,
	data: ChartDataConfig | undefined,
	options: BuildDefaultChartDataOrderOptions = {}
): OrderConfig {
	if (isTimeSeriesChartType(chartType)) {
		const key = timeDimensionOrderKey(data, options.timeDimensionField);
		return key ? { [key]: 'asc' } : {};
	}

	if (usesCategoryAxisChartType(chartType)) {
		const key = categoryDimensionOrderKey(
			data,
			options.categoryDimensionField
		);
		if (key) {
			const isGroupedCardDate =
				key === timeDimensionOrderKey(data, options.timeDimensionField) &&
				knownGranularity(data?.timeDimension?.granularity) !== undefined;
			return { [key]: isGroupedCardDate ? 'asc' : 'desc' };
		}
		const metric =
			options.metrics?.[0]?.name?.trim() ??
			data?.metrics?.[0]?.name?.trim();
		return metric ? { [metric]: 'desc' } : {};
	}

	const metric =
		options.metrics?.[0]?.name?.trim() ?? data?.metrics?.[0]?.name?.trim();
	if (metric) {
		return { [metric]: 'desc' };
	}
	const key = timeDimensionOrderKey(data, options.timeDimensionField);
	return key ? { [key]: 'asc' } : {};
}

/** Uses saved order when present; otherwise chart-type defaults. */
export function resolveChartDataOrder(
	chartType: string,
	data: ChartDataConfig | undefined,
	options: BuildDefaultChartDataOrderOptions = {}
): OrderConfig {
	const entries = validOrderEntries(normalizeOrderFromWire(data?.order));
	if (entries.length === 0) {
		return buildDefaultChartDataOrder(chartType, data, options);
	}

	const [key, direction] = entries[0];
	return { [key]: direction };
}
